import React, { useState, useEffect, useRef } from 'react';
import { FiLoader, FiCheckCircle, FiAlertCircle, FiRefreshCw, FiDatabase } from 'react-icons/fi';
import Modal from '../../../components/Modal';
import { crosswalkApi } from '../../../services/api';

const ACTIVE_STATUSES = ['queued', 'running'];
const POLL_MS = 3000;

const ENVIRONMENTS = [
  { value: 'staging', label: 'Staging' },
  { value: 'prod',    label: 'Production' },
  { value: 'test',    label: 'Test' },
];

const TARGETS = [
  { value: 'all',       label: 'All (questions + evidence)' },
  { value: 'questions', label: 'Questions only' },
];

const STATUS_STYLES = {
  queued:    'text-amber-600 bg-amber-50',
  running:   'text-amber-600 bg-amber-50',
  completed: 'text-green-600 bg-green-50',
  failed:    'text-red-600 bg-red-50',
};

/**
 * Admin tool to (re)index the crosswalk search indexes (risk-questions-index +
 * risk-evidence-chunks). A stale index is a common cause of crosswalk jobs returning
 * zero proposals. Triggers the job, then polls until it reaches a terminal state.
 */
const ReindexModal = ({ isOpen, onClose }) => {
  const [environment, setEnvironment] = useState('staging');
  const [target, setTarget] = useState('all');
  const [job, setJob] = useState(null);          // latest AssessmentIndexJob
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  // Reset state whenever the modal is opened.
  useEffect(() => {
    if (isOpen) {
      setJob(null);
      setError(null);
      setSubmitting(false);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isOpen]);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startReindex = async () => {
    setSubmitting(true);
    setError(null);
    setJob(null);
    stopPolling();

    try {
      const created = await crosswalkApi.startIndexJob({ environment, target });
      setJob(created);

      if (created?.jobId && ACTIVE_STATUSES.includes(created.status)) {
        pollRef.current = setInterval(async () => {
          try {
            const latest = await crosswalkApi.getIndexJob(created.jobId);
            if (latest) {
              setJob(latest);
              if (!ACTIVE_STATUSES.includes(latest.status)) stopPolling();
            }
          } catch {
            /* transient — retry next tick */
          }
        }, POLL_MS);
      }
    } catch (e) {
      setError(e?.message || 'Failed to start reindex job.');
    } finally {
      setSubmitting(false);
    }
  };

  const isActive = job && ACTIVE_STATUSES.includes(job.status);
  const qi = job?.result?.questionsIndexer;
  const re = job?.result?.riskEvidence;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { stopPolling(); onClose(); }}
      title="Reindex Crosswalk Search Indexes"
      size="md"
      footer={
        <>
          <button
            onClick={() => { stopPolling(); onClose(); }}
            className="px-4 py-2 text-sm font-medium border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={startReindex}
            disabled={submitting || isActive}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#F47F2E] text-white hover:bg-[#e06a1f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting || isActive
              ? <><FiLoader className="animate-spin" /> Reindexing…</>
              : <><FiRefreshCw /> {job ? 'Run Again' : 'Start Reindex'}</>}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          Refreshes the search indexes crosswalk matching depends on. Run this if crosswalk jobs
          complete with no proposals after adding answers or evidence documents.
        </p>

        {/* Options */}
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Environment</span>
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              disabled={isActive}
              className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:ring-[#0077b5] focus:border-[#0077b5] disabled:bg-slate-50"
            >
              {ENVIRONMENTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Target</span>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={isActive}
              className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:ring-[#0077b5] focus:border-[#0077b5] disabled:bg-slate-50"
            >
              {TARGETS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        </div>

        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Make sure <span className="font-semibold">Environment</span> matches where your live
          crosswalk data lives — indexing the wrong environment will not fix missing proposals.
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <FiAlertCircle /> {error}
          </div>
        )}

        {/* Job status */}
        {job && (
          <div className="space-y-3 border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                <FiDatabase className="text-slate-400" /> Index Job
              </span>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[job.status] || 'text-slate-600 bg-slate-50'}`}>
                {isActive
                  ? <><FiLoader className="animate-spin text-xs" /> {job.status}</>
                  : job.status === 'completed'
                    ? <><FiCheckCircle className="text-xs" /> completed</>
                    : job.status === 'failed'
                      ? <><FiAlertCircle className="text-xs" /> failed</>
                      : <>{job.status}</>}
              </span>
            </div>

            {job.jobId && (
              <p className="text-[11px] text-slate-400 font-mono truncate">Job ID: {job.jobId}</p>
            )}
            {(job.message || job.error) && (
              <p className={`text-xs ${job.error ? 'text-red-500' : 'text-slate-500'}`}>{job.error || job.message}</p>
            )}

            {/* Result breakdown */}
            {qi && (
              <div className="text-xs text-slate-600">
                <span className="font-medium">Questions indexer:</span> {qi.status}
                {typeof qi.itemsProcessed === 'number' && <> · {qi.itemsProcessed} processed</>}
                {typeof qi.itemsFailed === 'number' && qi.itemsFailed > 0 && <> · {qi.itemsFailed} failed</>}
              </div>
            )}
            {re && (
              <div className="text-xs text-slate-600">
                <span className="font-medium">Risk evidence:</span> {re.processedDocuments}/{re.sourceDocuments} docs
                · {re.chunkDocumentsUploaded} chunks uploaded
                {re.chunkDocumentsDeleted > 0 && <> · {re.chunkDocumentsDeleted} deleted</>}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReindexModal;
